import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import { useGetSupplierDetailLedgerQuery } from "../../../store/appApi";
import { selectHasModuleAccess } from "../../../store/authSlice";

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const hasAccess = useSelector(selectHasModuleAccess("suppliers", "read"));

  const { data, isLoading, isFetching, isError } =
    useGetSupplierDetailLedgerQuery({ id, page }, { skip: !hasAccess });

  const supplier = data?.supplier ?? {};
  const transactions = Array.isArray(data?.transactions)
    ? data.transactions
    : [];
  const grandTotal = Number(data?.grandTotal) || 0;
  const total = Number(data?.total) || 0;
  const loading = isLoading || isFetching;

  const PAGE_SIZE = 20;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fmt = (n) => Number(n).toLocaleString("en-IN");
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  if (!hasAccess) {
    return (
      <>
        <PageTitle activeMenu="Supplier Detail" motherMenu="Finance" />
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
                <h5>Access Restricted</h5>
                {/* <p className="text-muted mb-0">
                  Supplier expense details are only available to{" "}
                  <strong>Super Admin</strong> and <strong>Auditor</strong>{" "}
                  roles.
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Supplier Detail" motherMenu="Finance" />

      <div className="row">
        <div className="col-lg-12">
          {/* ── Supplier meta card ─────────────────────────────────────── */}
          <div
            className="card mb-3"
            style={{ background: "#f8f9fa", height: "120px" }}
          >
            <div className="card-body d-flex flex-wrap align-items-center gap-4">
              <div>
                <span
                  className="text-muted small d-block"
                  style={{ fontSize: "1rem" }}
                >
                  Supplier
                </span>
                <span className="fw-bold fs-5">
                  {loading ? "…" : supplier.name || "—"}
                </span>
              </div>
              <div className="ms-auto text-end">
                <span className="text-muted small d-block">Grand Total</span>
                <span className="fw-bold fs-5 text-danger">
                  ₹ {loading ? "…" : fmt(grandTotal)}
                </span>
              </div>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
            </div>
          </div>

          {/* ── Transaction table ──────────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Date-wise Expense Ledger</h5>
              <span className="badge badge-primary ms-2">{total}</span>
            </div>

            <div className="card-body">
              {isError && (
                <div className="alert alert-danger">
                  Failed to load supplier transactions. Please try again.
                </div>
              )}

              <div className="table-responsive">
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      {/* <th>#</th> */}
                      <th>Date</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Amount (₹)</th>
                      <th>Paid By / Received From</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}

                    {!loading && transactions.length === 0 && !isError && (
                      <tr>
                        <td colSpan={7} className="text-center py-4 text-muted">
                          No expense transactions found for this supplier.
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      transactions.map((txn, idx) => {
                        const isReceipt = txn.type === "receipt";
                        return (
                          <tr key={txn._id || idx}>
                            {/* <td className="text-muted small">
                              {(page - 1) * PAGE_SIZE + idx + 1}
                            </td> */}
                            <td>{fmtDate(txn.created)}</td>
                            <td>
                              <span
                                className={
                                  isReceipt
                                    ? "badge badge-success"
                                    : "badge badge-danger"
                                }
                                style={{ textTransform: "capitalize" }}
                              >
                                {txn.type}
                              </span>
                            </td>
                            <td className="text-muted small">
                              {isReceipt ? "—" : txn.categoryName || "—"}
                            </td>
                            <td
                              className={`fw-semibold ${isReceipt ? "text-success" : "text-danger"}`}
                            >
                              ₹ {fmt(txn.amount)}
                            </td>
                            <td style={{ textTransform: "capitalize" }}>
                              {isReceipt ? (
                                <span>
                                  <span className="text-muted small me-1">
                                    Received From:
                                  </span>
                                  {txn.paidBy || "—"}
                                </span>
                              ) : (
                                txn.paidBy || "—"
                              )}
                            </td>
                            <td className="text-muted small">
                              {txn.desc || "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination ── */}
              {!loading && totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">
                    Page {page} of {totalPages}
                  </span>
                  <div className="dataTables_paginate paging_simple_numbers">
                    <Link
                      to="#"
                      className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                      onClick={() => page > 1 && setPage(page - 1)}
                    >
                      Previous
                    </Link>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (n) => (
                        <Link
                          key={n}
                          to="#"
                          className={`paginate_button ${page === n ? "current" : ""}`}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </Link>
                      ),
                    )}
                    <Link
                      to="#"
                      className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                      onClick={() => page < totalPages && setPage(page + 1)}
                    >
                      Next
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupplierDetail;
